import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Logo from "@/components/Logo";
import RaceSetupForm from "@/components/race/RaceSetupForm";
import { toast } from "sonner";

// Coach builds — or edits — a race plan for one athlete.
export default function CoachRaceSetup() {
  const { athleteId, planId } = useParams();
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [plan, setPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const editing = !!planId;

  useEffect(() => {
    (async () => {
      let existing = null;
      if (planId) {
        try {
          const { data } = await api.get(`/coach-race/race-plans/${planId}`);
          existing = data;
          setPlan(data);
        } catch (e) {
          toast.error(apiError(e, "Could not open that race plan."));
          navigate("/coach");
          return;
        }
      }
      const id = athleteId || existing?.athleteId;
      try {
        const { data } = await api.get("/coach-race/athletes");
        setAthlete((data || []).find((a) => a.id === id)
          || { name: existing?.athleteName || "Athlete", profile: existing?.profile || {} });
      } catch {
        setAthlete({ name: existing?.athleteName || "Athlete", profile: {} });
      }
    })();
  }, [athleteId, planId, navigate]);

  const submit = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/coach-race/race-plans/${planId}`, payload);
        toast.success("Race plan updated.");
        navigate(`/coach/plans/${planId}`);
      } else {
        const { data } = await api.post(
          `/coach-race/athletes/${encodeURIComponent(athleteId)}/race-plans`, payload);
        toast.success("Race plan built.");
        navigate(`/coach/plans/${data.id}`);
      }
    } catch (e) {
      toast.error(apiError(e, "Could not save that race plan."));
      setSaving(false);
    }
  };

  if (!athlete || (editing && !plan)) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#E2E30A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-24" data-testid="coach-race-setup-page">
      <header className="border-b border-white/10">
        <div className="max-w-2xl mx-auto px-5 sm:px-8 h-16 flex items-center">
          <Link to="/coach" className="flex items-center gap-2.5">
            <Logo className="h-9 w-9" />
            <span className="font-display text-base uppercase tracking-tight">
              <span className="text-[#E2E30A]">Coach</span> Race Builder
            </span>
          </Link>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 sm:px-8 pt-10">
        <h1 className="font-display text-3xl uppercase tracking-tighter font-black">{athlete.name}</h1>
        <p className="text-sm text-zinc-400 mt-1 mb-8">
          {editing ? `Editing “${plan.name}” — change anything and save.`
            : "Building a race week + race day plan."}
        </p>
        <RaceSetupForm initialProfile={athlete.profile || {}}
          initialPlan={plan ? { ...plan, profile: { ...(plan.profile || {}), ...(athlete.profile || {}) } } : null}
          athleteName={athlete.name} profileLabel="Athlete numbers"
          submitLabel={editing ? "Save changes" : "Build race plan"}
          onSubmit={submit} saving={saving}
          onCancel={() => navigate(editing ? `/coach/plans/${planId}` : "/coach")} />
      </main>
    </div>
  );
}
